import React from 'react';

import { Box, HStack, Heading, InfoBadge, Label, List, Text, VStack } from '@medely/ui-kit';
import { MedelyDateTime } from '@medely/date-time-tools';
import { tz as momentTz } from 'moment-timezone';
import DisplayHelpers from '../../../utils/displayHelpers';
import { AddressLink } from '../AddressLink';
import { JobIrregularity } from '@mp/graphql/generated/graphql';
import { CompletedShiftGeneralInfoProps } from './interface';
import { DescriptionListItem } from '../../DescriptionListItem';
import { ShiftInReviewAlert } from './ShiftInReviewAlert';
import { useCurrentUser } from '@mp/hooks';

const { date, positions, shiftType } = DisplayHelpers.job;

const getClockEditInfo = (job: CompletedShiftGeneralInfoProps['job']) => {
  if (!job.active_self_service) {
    return {
      clockInEdited: false,
      clockOutEdited: false,
      editedClockIn: null as string | null,
      editedClockOut: null as string | null,
    };
  }
  const irregularities = (job.job_irregularities as JobIrregularity[]) ?? [];
  const openEdited = irregularities.filter(
    (irr: JobIrregularity) =>
      irr.irregularity_type === 'professional_edited_shift' && irr.status === 'open',
  );
  const clockInEdit = openEdited.find((irr) => irr.actual_clock_in !== irr.expected_clock_in);
  const clockOutEdit = openEdited.find((irr) => irr.actual_clock_out !== irr.expected_clock_out);
  return {
    clockInEdited: !!clockInEdit,
    clockOutEdited: !!clockOutEdit,
    editedClockIn: clockInEdit?.actual_clock_in ?? null,
    editedClockOut: clockOutEdit?.actual_clock_out ?? null,
  };
};

export const CompletedShiftGeneralInfo = ({ job }: CompletedShiftGeneralInfoProps) => {
  const jbSums = job.job_billing_sums;
  const isInReview = job.status === 'held_for_dispute_review';
  const { isSaas } = useCurrentUser();
  const { clockInEdited, clockOutEdited, editedClockIn, editedClockOut } = getClockEditInfo(job);
  const timezone = job?.location?.timezone_lookup ?? momentTz.guess();
  const startsTime = editedClockIn ?? job?.approved_clock_in ?? '';
  const endsTime = editedClockOut ?? job?.approved_clock_out ?? '';

  const clockInValue = startsTime
    ? new MedelyDateTime(startsTime, { tz: timezone }).militaryTime
    : null;
  const clockOutValue = endsTime
    ? new MedelyDateTime(endsTime, { tz: timezone }).militaryTime
    : null;

  const clockInDisplay = clockInEdited ? (
    <HStack gap={1}>
      <InfoBadge label="Updated" color="success" />
      <Text bold size="m">
        {clockInValue}
      </Text>
    </HStack>
  ) : (
    clockInValue
  );
  const clockOutDisplay = clockOutEdited ? (
    <HStack gap={1}>
      <InfoBadge label="Updated" color="success" />
      <Text bold size="m">
        {clockOutValue}
      </Text>
    </HStack>
  ) : (
    clockOutValue
  );

  const shiftDetailsItems = [
    { label: 'Date', value: date(job, 'short') },
    { label: 'Hours worked', value: jbSums.total_hours },
    { label: 'Clock in', bold: clockInEdited, value: clockInDisplay },
    { label: 'Clock out', bold: clockOutEdited, value: clockOutDisplay },
    {
      label: 'Meal break',
      value: !!jbSums.total_breaks_minutes ? `${jbSums.total_breaks_minutes} min` : 'Not taken',
    },
    { label: 'Shift type', value: shiftType(job) },
    ...(isSaas ? [] : [{ label: 'Tax type', value: job.is_w2 ? 'W-2' : '1099' }]),
    { label: 'Job ID', value: job.id },
  ];

  return (
    <VStack gap={2}>
      <Heading size="s">Shift details</Heading>
      <VStack>
        <Label size="m">{positions(job.positions)}</Label>
        <Box py={1}>
          <Text size="m">{job.location.name}</Text>
          <AddressLink location={job.location} />
        </Box>
      </VStack>
      <Box borderBottomWidth={1} borderColor="controls.backing+line" />
      {isInReview && <ShiftInReviewAlert jobId={job.id} />}
      <List items={shiftDetailsItems} keyFn={(item) => item.label} render={DescriptionListItem} />
    </VStack>
  );
};
